import { memo, useMemo } from 'react';
import { Link } from 'wouter';
import { useTranslation } from 'react-i18next';
import { AlertCircle, TrendingUp, Users, Calendar, CheckCircle2, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SubscriptionError } from '@/types/subscription';
import { cn } from '@/lib/utils';

interface UpgradeDialogProps {
  open: boolean;
  onClose: () => void;
  error: SubscriptionError;
}

export const UpgradeDialog = memo(({ open, onClose, error }: UpgradeDialogProps) => {
  const { t } = useTranslation('subscription');

  // Determine icon based on error type and limit
  const Icon = useMemo(() => {
    if (error.type === 'feature_disabled') {
      return XCircle;
    }
    if (error.limit === 'users') return Users;
    if (error.limit === 'bookings_this_month') return Calendar;
    return TrendingUp;
  }, [error.type, error.limit]);

  // Get user-friendly limit name
  const limitDisplayName = useMemo(() => {
    if (error.limitName) return error.limitName;
    if (error.limit === 'bookings_this_month') return t('limits.monthlyBookings');
    if (error.limit === 'users') return t('limits.users');
    if (error.limit === 'providers') return t('limits.providers');
    if (error.limit === 'services') return t('limits.services');
    return t('limits.resource');
  }, [error.limitName, error.limit, t]);

  // Feature benefits for feature_disabled errors
  const featureBenefits = useMemo(() => {
    if (!error.feature) return [];

    // Try to get benefits from translations
    try {
      const benefits = t(`upgradeDialog.features.${error.feature}.benefits`, { returnObjects: true });
      if (Array.isArray(benefits)) {
        return benefits;
      }
    } catch (e) {
      // Feature not in translations, return empty
    }

    return [];
  }, [error.feature, t]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div
              className={cn(
                'flex items-center justify-center w-12 h-12 rounded-full',
                error.type === 'feature_disabled'
                  ? 'bg-purple-100'
                  : 'bg-orange-100'
              )}
            >
              <Icon
                className={cn(
                  'w-6 h-6',
                  error.type === 'feature_disabled'
                    ? 'text-purple-600'
                    : 'text-orange-600'
                )}
              />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl">
                {error.type === 'subscription_limit' ? t('upgradeDialog.title.limitReached') : t('upgradeDialog.title.premiumFeature')}
              </DialogTitle>
              <Badge
                variant="outline"
                className="mt-1 text-xs font-normal border-muted-foreground/20"
              >
                {error.currentPlan ? t('upgradeDialog.current', { plan: error.currentPlan }) : t('upgradeDialog.title.upgradeRequired')}
              </Badge>
            </div>
          </div>
          <DialogDescription className="text-base text-foreground/80 pt-2">
            {error.message}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Limit Details - for subscription_limit type */}
          {error.type === 'subscription_limit' && error.current !== undefined && error.max !== undefined && (
            <div className="p-4 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  {t('upgradeDialog.usage', { limitName: limitDisplayName })}
                </span>
                <span className="text-sm font-semibold">
                  {error.current} / {error.max}
                </span>
              </div>
              <div className="w-full bg-background rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-300"
                  style={{ width: `${Math.min((error.current / error.max) * 100, 100)}%` }}
                />
              </div>
              {error.description && (
                <p className="mt-3 text-sm text-muted-foreground">{error.description}</p>
              )}
            </div>
          )}

          {/* Feature Benefits - for feature_disabled type */}
          {error.type === 'feature_disabled' && featureBenefits.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">
                {error.featureName ? t('upgradeDialog.unlockFeature', { featureName: error.featureName }) : t('upgradeDialog.unlockThisFeature')}
              </h4>
              <ul className="space-y-2">
                {featureBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Feature Description - for feature_disabled without predefined benefits */}
          {error.type === 'feature_disabled' && featureBenefits.length === 0 && error.description && (
            <div className="p-4 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">{error.description}</p>
              </div>
            </div>
          )}

          {/* Upgrade Recommendation */}
          {(error.requiredPlan || error.recommendedPlan) && (
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  {t('upgradeDialog.recommendedPlan')}
                </span>
              </div>
              <p className="text-sm text-foreground">
                {t('upgradeDialog.recommendedPlanDescription', { plan: error.recommendedPlan || error.requiredPlan })}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="sm:flex-1">
            {t('upgradeDialog.actions.maybeLater')}
          </Button>
          <Link href="/admin/billing" onClick={onClose}>
            <Button className="w-full sm:flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
              {error.recommendedPlan || error.requiredPlan
                ? t('upgradeDialog.actions.upgradeTo', { plan: error.recommendedPlan || error.requiredPlan })
                : t('upgradeDialog.actions.upgradeToPremium')
              }
            </Button>
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

UpgradeDialog.displayName = 'UpgradeDialog';
